import React, { useState } from "react";
import { CheckCircle2, CreditCard, Info } from "lucide-react";
import { useLang } from "../i18n";
import { postRegistration, createPayment } from "../lib/api";

// Mollie-betalingen tijdelijk on hold. Zet op true om betalingen bij inschrijving weer te activeren.
const PAYMENTS_ENABLED = false;

const THEMES = {
  "fc-inkad": {
    input: "w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-4 py-3 text-white placeholder-white/30 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] outline-none transition-colors",
    label: "text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block",
    accent: "#CCFF00",
    rowActive: "border-[#CCFF00] bg-[#CCFF00]/10",
    rowIdle: "border-white/15 hover:border-white/40",
    rowText: "text-white/90",
    tag: "bg-white/10 text-white/60",
    fee: "text-[#CCFF00]",
    notice: "border-[#CCFF00]/30 bg-[#CCFF00]/5 text-white/70",
    button: "w-full rounded-sm bg-[#CCFF00] px-6 py-4 font-extrabold uppercase text-black hover:scale-[1.02] transition-transform duration-150 disabled:opacity-50",
    successCard: "border border-[#CCFF00]/30 bg-[#141414] rounded-sm",
    successText: "font-anton text-2xl uppercase text-white",
    successIcon: "text-[#CCFF00]",
  },
  jeugd: {
    input: "w-full bg-[#F7F5F0] border border-[#E8E4DC] rounded-2xl px-4 py-3 text-[#262626] placeholder-[#52525B]/50 focus:border-[#E07A5F] focus:ring-1 focus:ring-[#E07A5F] outline-none transition-colors",
    label: "text-xs font-semibold text-[#52525B] mb-1.5 block",
    accent: "#E07A5F",
    rowActive: "border-[#E07A5F] bg-[#E07A5F]/8",
    rowIdle: "border-[#E8E4DC] hover:border-[#E07A5F]/50",
    rowText: "text-[#262626]",
    tag: "bg-[#262626]/10 text-[#52525B]",
    fee: "text-[#E07A5F]",
    notice: "border-[#E07A5F]/30 bg-[#E07A5F]/5 text-[#52525B]",
    button: "w-full rounded-full bg-[#E07A5F] px-6 py-4 font-bold text-white hover:scale-[1.02] transition-transform duration-150 disabled:opacity-50",
    successCard: "bg-white shadow-[0_8px_30px_rgba(0,0,0,0.05)] rounded-[2rem]",
    successText: "font-fredoka text-2xl font-semibold text-[#262626]",
    successIcon: "text-[#81B29A]",
  },
};

export const MembershipForm = ({ branch, programs, multiple = false }) => {
  const { t } = useLang();
  const th = THEMES[branch] || THEMES["fc-inkad"];
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", birth_date: "",
    gender: "", address: "", phone: "",
  });
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggle = (value) => {
    if (multiple) {
      setSelected((s) => (s.includes(value) ? s.filter((v) => v !== value) : [...s, value]));
    } else {
      setSelected([value]);
    }
  };

  const totalFee = selected.reduce((sum, v) => sum + (programs.find((p) => p.value === v)?.fee || 0), 0);
  const needsPayment = PAYMENTS_ENABLED && totalFee > 0;
  const hasOnRequest = selected.some((v) => programs.find((p) => p.value === v)?.onRequest);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) return;
    setStatus("sending");
    try {
      const reg = await postRegistration({ ...form, branch, programs: selected });
      if (reg.requires_payment) {
        const { checkout_url } = await createPayment(reg.id);
        (window.top || window).location.href = checkout_url;
        return;
      }
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className={`text-center p-10 ${th.successCard}`} data-testid="register-success">
        <CheckCircle2 size={48} className={`mx-auto mb-4 ${th.successIcon}`} />
        <p className={th.successText}>{t("regOk")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={th.label}>{t("fFirstName")} *</label><input required data-testid="reg-first-name" className={th.input} value={form.first_name} onChange={set("first_name")} /></div>
        <div><label className={th.label}>{t("fLastName")} *</label><input required data-testid="reg-last-name" className={th.input} value={form.last_name} onChange={set("last_name")} /></div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={th.label}>{t("fEmail")} *</label><input required type="email" data-testid="reg-email" className={th.input} value={form.email} onChange={set("email")} /></div>
        <div><label className={th.label}>{t("fPhone")} *</label><input required data-testid="reg-phone" className={th.input} value={form.phone} onChange={set("phone")} placeholder="0484 33 17 88" /></div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={th.label}>{t("fBirthDate")} *</label><input required type="date" data-testid="reg-birth-date" className={th.input} value={form.birth_date} onChange={set("birth_date")} /></div>
        <div>
          <label className={th.label}>{t("fGender")} *</label>
          <div className="flex gap-6 pt-3">
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${th.rowText}`}>
              <input type="radio" name="gender" required value="jongen" checked={form.gender === "jongen"} onChange={set("gender")} data-testid="reg-gender-jongen" style={{ accentColor: th.accent }} /> {t("fBoy")}
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${th.rowText}`}>
              <input type="radio" name="gender" value="meisje" checked={form.gender === "meisje"} onChange={set("gender")} data-testid="reg-gender-meisje" style={{ accentColor: th.accent }} /> {t("fGirl")}
            </label>
          </div>
        </div>
      </div>

      <div><label className={th.label}>{t("fAddress")} *</label><input required data-testid="reg-address" className={th.input} value={form.address} onChange={set("address")} placeholder="Kerktorenstraat 31, 1831 Diegem" /></div>

      <div>
        <label className={th.label}>{t("fRegisterFor")} *{multiple && <span className="ml-2 lowercase tracking-normal opacity-70">({t("multipleAllowed")})</span>}</label>
        <div className="mt-2 space-y-2" data-testid="reg-program-list">
          {programs.map((p) => {
            const active = selected.includes(p.value);
            return (
              <label key={p.value} data-testid={`reg-program-${p.value}`}
                className={`flex items-center gap-3 rounded-${branch === "jeugd" ? "xl" : "sm"} border px-4 py-3 cursor-pointer transition-colors ${active ? th.rowActive : th.rowIdle}`}>
                <input type={multiple ? "checkbox" : "radio"} name="program" value={p.value} checked={active} onChange={() => toggle(p.value)} style={{ accentColor: th.accent }} />
                <span className={`flex-1 text-sm ${th.rowText}`}>{p.label}</span>
                {p.full && <span className={`rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-wider ${th.tag}`}>{t("volzetTag")}</span>}
                {PAYMENTS_ENABLED && <span className={`text-xs font-bold ${th.fee}`}>{p.onRequest ? t("feeOnRequest") : `€${p.fee}`}</span>}
              </label>
            );
          })}
        </div>
      </div>

      {hasOnRequest && (
        <div className={`flex items-start gap-3 rounded-${branch === "jeugd" ? "2xl" : "sm"} border ${th.notice} p-4 text-sm`} data-testid="reg-onrequest-note">
          <Info size={18} className="mt-0.5 shrink-0" style={{ color: th.accent }} />
          <span>{t("huiswerkNote")}</span>
        </div>
      )}

      {needsPayment && (
        <div className={`flex items-start gap-3 rounded-${branch === "jeugd" ? "2xl" : "sm"} border ${th.notice} p-4 text-sm`} data-testid="reg-fee-notice">
          <CreditCard size={18} className="mt-0.5 shrink-0" style={{ color: th.accent }} />
          <span>{t("feeNotice").replace("{amt}", totalFee)}</span>
        </div>
      )}

      {status === "error" && <p className="text-red-500 text-sm" data-testid="reg-error">{t("regErr")}</p>}

      <button type="submit" disabled={status === "sending" || !selected.length} data-testid="reg-submit" className={th.button}>
        {status === "sending" ? t("sending") : needsPayment ? t("submitPay").replace("{amt}", totalFee) : t("submit")}
      </button>
    </form>
  );
};
